import { format, parseISO } from 'date-fns'
import { th } from 'date-fns/locale'

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/common/ui/table'
import { Skeleton } from '@/common/ui/skeleton'
import { formatNumber } from '@/common/lib/utils'
import useQueryGetPartnerPaymentRecordById from '@/modules/manage-player/api/hooks/useQueryGetPartnerPaymentRecordById'

interface PaymentRecordDetailDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	partnerId: string
	recordId: string | null
}

export default function PaymentRecordDetailDialog({
	open,
	onOpenChange,
	partnerId,
	recordId,
}: PaymentRecordDetailDialogProps) {
	const { data: record, isLoading } = useQueryGetPartnerPaymentRecordById(
		partnerId,
		recordId || '',
		{
			enabled: !!partnerId && !!recordId && open,
		},
	)

	const snapshot = record?.snapshotData
	const players = snapshot?.players || []
	const summary = snapshot?.summary

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-h-[80vh] max-w-4xl overflow-y-auto">
				<DialogHeader>
					<DialogTitle>รายละเอียดการจ่ายส่วนแบ่ง</DialogTitle>
				</DialogHeader>

				{isLoading ? (
					<div className="space-y-4">
						<Skeleton className="h-20 w-full" />
						<Skeleton className="h-40 w-full" />
					</div>
				) : record ? (
					<div className="space-y-6">
						{/* Summary Section */}
						<div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
							<div className="rounded-lg border p-3">
								<p className="text-xs text-muted-foreground">ช่วงเวลา</p>
								<p className="font-medium">
									{format(parseISO(record.startDate), 'd MMM yy', {
										locale: th,
									})}
									{' - '}
									{format(parseISO(record.endDate), 'd MMM yy', { locale: th })}
								</p>
							</div>
							<div className="rounded-lg border p-3">
								<p className="text-xs text-muted-foreground">
									จำนวนเงินที่จ่าย
								</p>
								<p className="font-bold text-green-600">
									{formatNumber(record.amount)}
								</p>
							</div>
							<div className="rounded-lg border p-3">
								<p className="text-xs text-muted-foreground">จำนวนผู้เล่น</p>
								<p className="font-medium">{summary?.playerCount || 0} คน</p>
							</div>
							<div className="rounded-lg border p-3">
								<p className="text-xs text-muted-foreground">ผู้บันทึก</p>
								<p className="font-medium">{record.createdByUsername || '-'}</p>
							</div>
						</div>

						{/* Summary Totals */}
						<div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
							<div className="rounded-lg bg-green-50 p-3 dark:bg-green-900/20">
								<p className="text-xs text-muted-foreground">ยอดฝากรวม</p>
								<p className="font-bold text-green-600">
									{formatNumber(summary?.totalDepositAmount || 0)}
								</p>
							</div>
							<div className="rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20">
								<p className="text-xs text-muted-foreground">โบนัสรวม</p>
								<p className="font-bold text-blue-600">
									{formatNumber(summary?.totalDepositBonusAmount || 0)}
								</p>
							</div>
							<div className="rounded-lg bg-red-50 p-3 dark:bg-red-900/20">
								<p className="text-xs text-muted-foreground">ยอดถอนรวม</p>
								<p className="font-bold text-red-600">
									{formatNumber(summary?.totalWithdraw || 0)}
								</p>
							</div>
							<div className="rounded-lg bg-primary/10 p-3">
								<p className="text-xs text-muted-foreground">ส่วนแบ่งรวม</p>
								<p className="font-bold text-primary">
									{formatNumber(summary?.totalPartnerShare || 0)}
								</p>
							</div>
						</div>

						{/* Note */}
						{record.note && (
							<div className="rounded-lg border p-3">
								<p className="text-xs text-muted-foreground">หมายเหตุ</p>
								<p className="mt-1">{record.note}</p>
							</div>
						)}

						{/* Players Table */}
						<div>
							<h4 className="mb-3 font-medium">รายละเอียดผู้เล่น</h4>
							{players.length === 0 ? (
								<div className="py-8 text-center text-muted-foreground">
									ไม่มีข้อมูลผู้เล่น
								</div>
							) : (
								<div className="rounded-md border">
									<Table>
										<TableHeader>
											<TableRow>
												<TableHead>เบอร์โทร</TableHead>
												<TableHead>วันที่สมัคร</TableHead>
												<TableHead className="text-right">ยอดฝาก</TableHead>
												<TableHead className="text-right">โบนัส</TableHead>
												<TableHead className="text-right">ยอดถอน</TableHead>
												<TableHead className="text-right">ส่วนแบ่ง</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{players.map((player) => (
												<TableRow key={player.playerId}>
													<TableCell className="font-medium">
														{player.phone}
													</TableCell>
													<TableCell>
														{format(parseISO(player.createdAt), 'd MMM yy', {
															locale: th,
														})}
													</TableCell>
													<TableCell className="text-right text-green-600">
														{formatNumber(player.depositAmount)}
													</TableCell>
													<TableCell className="text-right text-blue-600">
														{formatNumber(player.depositBonusAmount)}
													</TableCell>
													<TableCell className="text-right text-red-600">
														{formatNumber(player.totalWithdraw)}
													</TableCell>
													<TableCell className="text-right font-medium text-primary">
														{formatNumber(player.partnerShare)}
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</div>
							)}
						</div>

						{/* Created At */}
						<div className="text-right text-xs text-muted-foreground">
							บันทึกเมื่อ:{' '}
							{format(parseISO(record.createdAt), 'd MMM yy HH:mm', {
								locale: th,
							})}
						</div>
					</div>
				) : (
					<div className="py-8 text-center text-muted-foreground">
						ไม่พบข้อมูล
					</div>
				)}
			</DialogContent>
		</Dialog>
	)
}
